import type { ActivityLevel, Goal, Sex } from '@/types';

/**
 * Расчёт BMR по формуле Mifflin-St Jeor.
 * Признана наиболее точной для большинства взрослых.
 */
export function calculateBMR(params: {
  sex: Sex;
  weightKg: number;
  heightCm: number;
  ageYears: number;
}): number {
  const { sex, weightKg, heightCm, ageYears } = params;
  const base = 10 * weightKg + 6.25 * heightCm - 5 * ageYears;
  return Math.round(sex === 'male' ? base + 5 : base - 161);
}

const ACTIVITY_FACTOR: Record<ActivityLevel, number> = {
  sedentary: 1.2,    // мало/нет тренировок, офис
  light: 1.375,      // 1–3 дня в неделю лёгкие
  moderate: 1.55,    // 3–5 дней средние
  active: 1.725,     // 6–7 дней интенсивные
  very_active: 1.9,  // спортсмены / физический труд
};

/**
 * TDEE = BMR × коэффициент активности
 */
export function calculateTDEE(bmr: number, level: ActivityLevel): number {
  return Math.round(bmr * ACTIVITY_FACTOR[level]);
}

/**
 * Целевые калории под цель.
 */
export function calculateGoalCalories(
  tdee: number,
  goal: Goal,
  sex: Sex,
): number {
  let target = tdee;
  if (goal === 'lose') target = Math.round(tdee * 0.8);
  if (goal === 'gain') target = Math.round(tdee * 1.15);
  const floor = sex === 'female' ? 1200 : 1500;
  return Math.max(target, floor);
}

/**
 * Распределение БЖУ.
 */
export function calculateMacros(params: {
  calories: number;
  weightKg: number;
  heightCm?: number;
  goal: Goal;
}): { protein_g: number; fat_g: number; carbs_g: number } {
  const { calories, weightKg, heightCm, goal } = params;
  // For weight loss, cap the protein calculation weight at adult BMI 24.9.
  // This is a reference for macros, not the person's chosen goal weight.
  // Energy needs still use actual weight in calculateBMR.
  // DGE: use normal weight to calculate protein in adults with overweight:
  // https://www.dge.de/wissenschaft/referenzwerte/protein/ (footnote a).
  // The BMI 24.9 cap and existing 1.8 g/kg coefficient are app policy.
  const referenceWeightKg = typeof heightCm === 'number'
    && Number.isFinite(heightCm) && heightCm >= 100 && heightCm <= 250
    ? 24.9 * (heightCm / 100) ** 2
    : weightKg;
  const proteinWeightKg = goal === 'lose' ? Math.min(weightKg, referenceWeightKg) : weightKg;
  const proteinPerKg = goal === 'lose' ? 1.8 : goal === 'gain' ? 1.6 : 1.4;
  const protein_g = Math.round(proteinWeightKg * proteinPerKg);
  const fat_g = Math.round((calories * 0.25) / 9);
  const proteinCals = protein_g * 4;
  const fatCals = fat_g * 9;
  const carbs_g = Math.max(0, Math.round((calories - proteinCals - fatCals) / 4));
  return { protein_g, fat_g, carbs_g };
}

/** Возраст по дате рождения */
export function ageFromBirthDate(iso: string): number {
  const birth = new Date(iso);
  const now = new Date();
  let age = now.getFullYear() - birth.getFullYear();
  const m = now.getMonth() - birth.getMonth();
  if (m < 0 || (m === 0 && now.getDate() < birth.getDate())) age--;
  return age;
}

/**
 * 🆕 Получить эффективный BMR с учётом кастомного значения.
 * Если у юзера установлен custom_bmr и use_custom_bmr = true — используется он.
 * Иначе — формула Mifflin-St Jeor.
 */
export function getEffectiveBMR(p: {
  sex: Sex;
  birth_date: string;
  height_cm: number;
  weight_kg: number;
  custom_bmr?: number | null;
  use_custom_bmr?: boolean | null;
}): { bmr: number; isCustom: boolean } {
  if (p.use_custom_bmr && p.custom_bmr && p.custom_bmr > 0) {
    return { bmr: p.custom_bmr, isCustom: true };
  }
  
  const ageYears = ageFromBirthDate(p.birth_date);
  const bmr = calculateBMR({
    sex: p.sex,
    weightKg: p.weight_kg,
    heightCm: p.height_cm,
    ageYears,
  });
  return { bmr, isCustom: false };
}

/**
 * Полный пайплайн: антропометрия → дневные нормы.
 * Теперь учитывает custom_bmr если он установлен.
 */
export function buildDailyTargets(p: {
  sex: Sex;
  birth_date: string;
  height_cm: number;
  weight_kg: number;
  goal: Goal;
  activity_level: ActivityLevel;
  custom_bmr?: number | null;
  use_custom_bmr?: boolean | null;
}) {
  const { bmr } = getEffectiveBMR(p);
  const tdee = calculateTDEE(bmr, p.activity_level);
  const calories = calculateGoalCalories(tdee, p.goal, p.sex);
  const macros = calculateMacros({
    calories,
    weightKg: p.weight_kg,
    heightCm: p.height_cm,
    goal: p.goal,
  });
  return {
    daily_calories: calories,
    daily_protein_g: macros.protein_g,
    daily_fat_g: macros.fat_g,
    daily_carbs_g: macros.carbs_g,
  };
}
